import { PriorityOneActivityName } from '../../../../models/PriorityOneActivityName'
import { Box, Stack, Typography } from '@mui/material'
import {
  StartTrainingAttendancePlanList
} from '../priorityActivities/PriorityOne/StartTrainingAttendancePlanList'
import {
  StartLeadershipDayAndConferenceList
} from '../priorityActivities/PriorityOne/StartLeadershipDayAndConferenceList'
import {
  StartCommunityOfPracticeList
} from '../priorityActivities/PriorityOne/StartCommunityOfPracticeList'
import { PriorityTwoActivityName } from '../../../../models/PriorityTwoActivityName'
import { PriorityThreeActivityName } from '../../../../models/PriorityThreeActivityName'
import { PriorityFourActivityName } from '../../../../models/PriorityFourActivityName'
import { EPLITrainerDevelopmentPlanList } from '../EPLITrainerDevelopmentPlanList'
import { ResourcesList } from '../priorityActivities/PriorityOne/ProfessionalDevelopmentResource/ResourcesList'
import {
  CSDevelopmentChecklistList
} from '../priorityActivities/priorityTwo/CSDevelopmentChecklistList'
import { type AnnualPlanRole } from '../../../../models/AnnualPlanRole'
import {
  CetaClassroomsAndFocusStudentsList
} from '../priorityActivities/PriorityThree/CetaClassroomsAndFocusStudentsList'
import { FocusBuildingsList } from '../priorityActivities/priorityFour/focusBuilding/FocusBuildingsList'
import {
  IsdDistrictProgramEnhancementsList
} from '../priorityActivities/priorityFour/IsdDistrictProgramEnhancementsList'
import { SupportCosts } from '../priorityActivities/rcnOperations/SupportCosts'
import { MeetingSchedules } from '../MeetingSchedules'
import { TrainingList } from '../priorityActivities/PriorityOne/TrainingList'
import { useTrainings } from '../../../../hooks/use-trainings'

interface Props {
  name: string
  planOrganizationId: number
  priorityId: number
  annualPlanId: number
  rcnId: number
  userRole: AnnualPlanRole | null
  hideButtons?: boolean
  implementationPhase?: boolean
}
export const BudgetActivityDetails = (props: Props): JSX.Element => {
  if (props.name === 'Meetings & RCN Support Costs') {
    return <Stack mt='14px' gap='30px'>
      <MeetingSchedules
        color='var(--white)'
        status=''
        annualPlanId={props.annualPlanId}
        rcnId={props.rcnId}
        readOnly={true}
        showTableOnly={true}
        isImplementationPhase={props.implementationPhase}
        trackingReadOnly={true}
        budgetOverviewView={true}
      />
      <SupportCosts
        color='var(--white)'
        status=''
        annualPlanId={props.annualPlanId}
        rcnId={props.rcnId}
        readOnly={true}
        showTableOnly={true}
        isImplementationPhase={props.implementationPhase}
        trackingReadOnly={true}
        budgetOverviewView={true}
      />
    </Stack>
  }

  const activityDetails = getActivityDetails(props)

  return <>
    {activityDetails != null && (
      <Box sx={{ backgroundColor: 'var(--white)', padding: '20px', borderRadius: '10px' }}>
        <Stack gap='30px'>
          {activityDetails}
        </Stack>
      </Box>
    )}
  </>
}

const getActivityDetails = (props: Props): JSX.Element | null => {
  switch (props.name) {
    case PriorityOneActivityName.RcnProvidedProfessionalDevelopment: {
      const { singleTrainings, seriesTrainings, isLoading, reloadTrainings } = useTrainings(props.priorityId, props.implementationPhase)
      return <>
        <TrainingList
          isSeries={true}
          trainings={seriesTrainings}
          isLoading={isLoading}
          reloadTrainings={reloadTrainings}
          planOrganizationId={props.planOrganizationId}
          priorityId={props.priorityId}
          readOnly={true}
          trackingReadonly={true}
          showTableOnly={true}
          hideExpand={props.hideButtons}
          implementationPhase={props.implementationPhase}
          budgetOverviewView={true}
        />

        <TrainingList
          isSeries={false}
          trainings={singleTrainings}
          isLoading={isLoading}
          reloadTrainings={reloadTrainings}
          planOrganizationId={props.planOrganizationId}
          priorityId={props.priorityId}
          readOnly={true}
          trackingReadonly={true}
          showTableOnly={true}
          hideExpand={props.hideButtons}
          implementationPhase={props.implementationPhase}
          budgetOverviewView={true}
        />
      </> }
    case PriorityOneActivityName.StartEventAttendancePlan:
      return <>
        <StartTrainingAttendancePlanList
          implementationPhase={props.implementationPhase ?? false}
          priorityId={props.priorityId}
          annualPlanId={props.annualPlanId}
          rcnId={props.rcnId}
          readOnly={true}
          trackingReadonly={true}
          showTableOnly={true}
          hideExpand={props.hideButtons}
          budgetOverviewView={true}
        />

        <StartLeadershipDayAndConferenceList
          implementationPhase={props.implementationPhase ?? false}
          priorityId={props.priorityId}
          readOnly={true}
          trackingReadonly={true}
          showTableOnly={true}
          hideExpand={props.hideButtons}
          budgetOverviewView={true}
        />

        <StartCommunityOfPracticeList
          implementationPhase={props.implementationPhase ?? false}
          priorityId={props.priorityId}
          annualPlanId={props.annualPlanId}
          readOnly={true}
          trackingReadonly={true}
          showTableOnly={true}
          hideExpand={props.hideButtons}
          budgetOverviewView={true}
        />
      </>
    case PriorityOneActivityName.EpliPlan:
      return <Stack gap='1em'>
        <Typography variant='h4'>EPLI Trainer Plan</Typography>
        <EPLITrainerDevelopmentPlanList
          priorityId={props.priorityId}
          annualPlanId={props.annualPlanId}
          orgId={props.rcnId}
          readOnly={true}
          trackingReadonly={true}
          showTableOnly={true}
          hideExpand={props.hideButtons}
          planOrgId={props.planOrganizationId}
          implementationPhase={props.implementationPhase}
          budgetOverviewView={true}
        />
      </Stack>
    case PriorityOneActivityName.OtherProfessionalDevelopmentResources:
      return <Stack gap='1em'>
        <Typography variant='h4'>Other Professional Development Resources</Typography>
        <ResourcesList
          priorityId={props.priorityId}
          readOnly={true}
          trackingReadonly={true}
          showTableOnly={true}
          hideExpand={props.hideButtons}
          implementationPhase={props.implementationPhase}
          budgetOverviewView={true}
        />
      </Stack>
    case PriorityTwoActivityName.CoachingStructureAndSystems:
      return <Stack gap='1em'>
        <Typography variant='h4'>Coaching Structure and Systems</Typography>
        <CSDevelopmentChecklistList
          userRole={props.userRole}
          priorityId={props.priorityId}
          planOrgId={props.planOrganizationId}
          onBack={() => {}}
          readOnly={true}
          trackingReadonly={true}
          showTableOnly={true}
          hideExpand={props.hideButtons}
          implementationPhase={props.implementationPhase}
          budgetOverviewView={true}
        />
      </Stack>
    case PriorityThreeActivityName.CetaClassroomsAndFocusStudents:
      return <Stack gap='1em'>
        <Typography variant='h4'>CETA Classrooms & Focus Students</Typography>
        <CetaClassroomsAndFocusStudentsList
          priorityId={props.priorityId}
          planOrgId={props.planOrganizationId}
          isdOrgId={0} // Only used for editing, not needed for read-only
          readOnly={true}
          trackingReadonly={true}
          showTableOnly={true}
          hideExpand={props.hideButtons}
          implementationPhase={props.implementationPhase}
          budgetOverviewView={true}
        />
      </Stack>
    case PriorityFourActivityName.FocusBuildings:
      return <Stack gap='1em'>
        <Typography variant='h4'>Focus Buildings</Typography>
        <FocusBuildingsList
          priorityId={props.priorityId}
          planOrgId={props.planOrganizationId}
          isdOrgId={0} // Only used for editing, not needed for read-only
          userRole={props.userRole}
          readOnly={true}
          trackingReadonly={true}
          showTableOnly={true}
          hideExpand={props.hideButtons}
          implementationPhase={props.implementationPhase}
          budgetOverviewView={true}
        />
      </Stack>
    case PriorityFourActivityName.ProgramEnrichment:
      return <Stack gap='1em'>
        <Typography variant='h4'>ISD/District Program Enhancements</Typography>
        <IsdDistrictProgramEnhancementsList
          priorityId={props.priorityId}
          planOrgId={props.planOrganizationId}
          isdOrgId={0}
          readOnly={true}
          trackingReadonly={true}
          showTableOnly={true}
          implementationPhase={props.implementationPhase}
          budgetOverviewView={true}
        />
      </Stack>
  }

  return null
}
